import { html, nothing, type TemplateResult } from "lit-html";
import QRCode from "qrcode";

import { api, ApiError } from "../api.js";
import {
  clearUnlocked,
  computeMasterPasswordHash,
  deriveKekForLogin,
  rewrapAskForPasswordChange,
  type UnlockedState,
} from "../client-crypto.js";
import { fromBase64, toBase64 } from "@shared/crypto/bytes.js";
import { copyToClipboard, rerender, setRoute, setUser, showToast, state as appState } from "../main.js";

const s = {
  busy: false,
  totpUri: "",
  totpSecret: "",
  totpCode: "",
  qrDataUrl: "",
  totpEnrolled: false,
  rcStatus: null as { remaining: number; total: number } | null,
  rcRegenCode: "",
  rcNewCodes: [] as string[],
  rcShowRegen: false,
  rcLoaded: false,
  passkeyLabel: "",
  // Change password state
  pwOld: "",
  pwNew: "",
  pwConfirm: "",
  pwErr: "",
};

async function loadRecoveryStatus(): Promise<void> {
  if (s.rcLoaded) return;
  s.rcLoaded = true;
  try {
    s.rcStatus = await api.recoveryCodesStatus();
    rerender();
  } catch {
    // SU-only endpoint; non-SU silently ignore.
  }
}

/**
 * (Removed) WebAuthn passkey enrollment. The product now uses Microsoft
 * Entra-style fingerprint + device_id cookie auto-recognition handled
 * by login.ts; no manual passkey enrollment is required.
 */
function _removedPasskeyDocs() { return null; }
/**
 * (Legacy, dead code kept for reference.) Manual WebAuthn passkey
 * enrollment. Replaced by automatic fingerprint + device cookie flow
 * in login.ts. This function is no longer wired to any UI control.
 *
 * We use the browser's PublicKeyCredential.create() API directly with
 * options from /api/devices/register-init. The server response is
 * forwarded as JSON to /api/devices/register-finish. The new device
 * starts as "pending" and an SU/Admin must approve it from the admin
 * console (unless this is the first device of the user, in which case
 * the chain-of-trust logic in approve.ts may permit self-approval if a
 * trust anchor exists).
 */
async function enrollPasskey(deviceLabel: string): Promise<void> {
  if (s.busy) return;
  if (!("credentials" in navigator) || !("create" in navigator.credentials)) {
    showToast(appState.lang === "en" ? "Passkeys not supported in this browser" : "このブラウザはパスキーに対応していません", "error");
    return;
  }
  s.busy = true;
  rerender();
  try {
    const initRes = await api.deviceRegisterInit();
    const opts = (initRes as { options: PublicKeyCredentialCreationOptions & { challenge: string; user: { id: string } } }).options;
    // The server returns challenge + user.id as base64url strings; the
    // WebAuthn API needs ArrayBuffer. Convert.
    const challenge = base64UrlToBytes(opts.challenge as unknown as string);
    const userId = base64UrlToBytes((opts.user as unknown as { id: string }).id);
    const creationOpts: PublicKeyCredentialCreationOptions = {
      ...opts,
      challenge: challenge.buffer as ArrayBuffer,
      user: { ...(opts.user as PublicKeyCredentialUserEntity), id: userId.buffer as ArrayBuffer },
    };
    const cred = await navigator.credentials.create({ publicKey: creationOpts }) as PublicKeyCredential | null;
    if (!cred) throw new Error("Credential creation returned null");
    const attestation = cred.response as AuthenticatorAttestationResponse;
    const payload = {
      id: cred.id,
      rawId: bytesToBase64Url(new Uint8Array(cred.rawId)),
      type: cred.type,
      response: {
        clientDataJSON: bytesToBase64Url(new Uint8Array(attestation.clientDataJSON)),
        attestationObject: bytesToBase64Url(new Uint8Array(attestation.attestationObject)),
      },
    };
    const result = await api.deviceRegisterFinish(payload, deviceLabel || undefined);
    showToast(appState.lang === "en"
      ? `Passkey enrolled (${result.status}). ${result.status === "pending" ? "Awaiting admin approval." : ""}`
      : `パスキーを登録しました (${result.status})。${result.status === "pending" ? "管理者の承認待ちです。" : ""}`);
    s.passkeyLabel = "";
  } catch (err) {
    showToast((err as Error).message, "error");
  } finally {
    s.busy = false;
    rerender();
  }
}

function base64UrlToBytes(b64u: string): Uint8Array {
  const b64 = b64u.replace(/-/g, "+").replace(/_/g, "/").padEnd(Math.ceil(b64u.length / 4) * 4, "=");
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

function bytesToBase64Url(bytes: Uint8Array): string {
  let s = "";
  for (let i = 0; i < bytes.length; i++) s += String.fromCharCode(bytes[i]!);
  return btoa(s).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

async function changePassword(): Promise<void> {
  if (s.busy) return;
  s.pwErr = "";
  if (s.pwNew.length < 12) {
    s.pwErr = appState.lang === "en" ? "New password must be at least 12 chars" : "新しいパスワードは 12 文字以上必要です";
    rerender(); return;
  }
  if (s.pwNew !== s.pwConfirm) {
    s.pwErr = appState.lang === "en" ? "Passwords do not match" : "パスワードが一致しません";
    rerender(); return;
  }
  const unlocked = (window as unknown as { __unlocked?: UnlockedState }).__unlocked;
  if (!unlocked || !appState.user) {
    s.pwErr = appState.lang === "en" ? "Vault is locked — log in again" : "Vault がロックされています — 再ログインしてください";
    rerender(); return;
  }
  s.busy = true;
  rerender();
  try {
    // Verify OLD password by re-deriving its KEK against the server-stored
    // salt + computing its master_password_hash. If it doesn't match the
    // current unlocked.kek-derived MPH the server will reject.
    const kdf = await api.kdfParams(appState.user.email);
    const oldKek = await deriveKekForLogin({
      password: s.pwOld,
      kdfSalt: fromBase64(kdf.kdf_salt_b64),
      kdfParams: kdf.kdf_params as { algo: "argon2id"; m: number; t: number; p: number; hashLength: number },
    });
    const oldMph = computeMasterPasswordHash({ kek: oldKek, kdfSalt: fromBase64(kdf.kdf_salt_b64) });

    const rotation = await rewrapAskForPasswordChange({
      currentUnlocked: unlocked,
      newPassword: s.pwNew,
    });

    await api.changePassword({
      old_master_password_hash_b64: toBase64(oldMph),
      new_kdf_salt_b64: toBase64(rotation.newKdfSalt),
      new_kdf_params_json: rotation.newKdfParamsJson,
      new_wrapped_ask_b64: rotation.newWrappedAskB64,
      new_master_password_hash_b64: rotation.newMasterPasswordHashB64,
    });

    // Server revoked our session along with all others. Clean local
    // state, drop the unlocked material from memory + sessionStorage,
    // and bounce to login.
    clearUnlocked();
    setUser(null);
    s.pwOld = ""; s.pwNew = ""; s.pwConfirm = "";
    showToast(appState.lang === "en"
      ? "Password changed. Please sign in with the new password."
      : "パスワードを変更しました。新しいパスワードでサインインしてください。");
    setRoute("login");
  } catch (err) {
    s.pwErr = err instanceof ApiError ? err.message : (err as Error).message;
  } finally {
    s.busy = false;
    rerender();
  }
}

async function regenerateRecoveryCodes(): Promise<void> {
  if (s.busy || !/^\d{6}$/.test(s.rcRegenCode)) return;
  if (!confirm(appState.lang === "en"
    ? "Generate a fresh set of recovery codes? Your existing codes will be invalidated."
    : "新しいリカバリコードを生成しますか？既存のコードは無効になります。")) return;
  s.busy = true;
  rerender();
  try {
    const r = await api.recoveryCodesRegenerate(s.rcRegenCode);
    s.rcNewCodes = r.codes;
    s.rcRegenCode = "";
    s.rcLoaded = false;
    await loadRecoveryStatus();
    showToast(appState.lang === "en"
      ? "Recovery codes regenerated. Save them now."
      : "リカバリコードを再生成しました。今すぐ保存してください。");
  } catch (err) {
    showToast((err as Error).message, "error");
  } finally {
    s.busy = false;
    rerender();
  }
}

async function enrollTotp(): Promise<void> {
  if (s.busy) return;
  s.busy = true;
  rerender();
  try {
    const r = await api.totpEnrollInit();
    s.totpUri = r.otpauth_uri;
    s.totpSecret = r.secret_b32;
    s.qrDataUrl = await QRCode.toDataURL(r.otpauth_uri, {
      width: 256,
      margin: 1,
      color: { dark: "#0a0a0f", light: "#ffffff" },
      errorCorrectionLevel: "M",
    });
  } catch (err) {
    showToast((err as Error).message, "error");
  } finally {
    s.busy = false;
    rerender();
  }
}

async function verifyTotp(): Promise<void> {
  if (s.busy || !s.totpCode) return;
  s.busy = true;
  rerender();
  try {
    await api.totpEnrollVerify(s.totpCode);
    showToast(appState.lang === "en" ? "2FA enabled" : "2FA を有効にしました");
    s.totpUri = "";
    s.totpSecret = "";
    s.totpCode = "";
    s.qrDataUrl = "";
    s.totpEnrolled = true;
  } catch (err) {
    showToast(err instanceof ApiError ? err.message : (err as Error).message, "error");
  } finally {
    s.busy = false;
    rerender();
  }
}

function changePasswordPanel(): TemplateResult {
  return html`
    <section class="card-panel">
      <h2 class="section-h2">
        <span data-lang="en">Change master password</span><span data-lang="jp">マスターパスワードの変更</span>
      </h2>
      <p class="section-help">
        <span data-lang="en">Rotates your master password. Your vault items remain accessible (the internal key does not change). All sessions on every device are revoked; you must sign in again.</span><span data-lang="jp">マスターパスワードを変更します。Vault のアイテムは引き続きアクセス可能です (内部鍵は変わりません)。すべてのデバイスのセッションが取り消され、再度サインインが必要になります。</span>
      </p>
      <div class="form-row" style="max-width: 420px;">
        <label><span data-lang="en">Current master password</span><span data-lang="jp">現在のマスターパスワード</span></label>
        <input type="password" autocomplete="current-password" .value=${s.pwOld}
          @input=${(e: InputEvent) => { s.pwOld = (e.target as HTMLInputElement).value; rerender(); }} />
      </div>
      <div class="form-row" style="max-width: 420px;">
        <label><span data-lang="en">New master password (12+ chars)</span><span data-lang="jp">新しいマスターパスワード (12 文字以上)</span></label>
        <input type="password" autocomplete="new-password" .value=${s.pwNew}
          @input=${(e: InputEvent) => { s.pwNew = (e.target as HTMLInputElement).value; rerender(); }} />
      </div>
      <div class="form-row" style="max-width: 420px;">
        <label><span data-lang="en">Confirm new password</span><span data-lang="jp">新しいパスワード (確認)</span></label>
        <input type="password" autocomplete="new-password" .value=${s.pwConfirm}
          @input=${(e: InputEvent) => { s.pwConfirm = (e.target as HTMLInputElement).value; rerender(); }} />
      </div>
      ${s.pwErr ? html`<p style="color: var(--status-error); font-size: 13px; margin: 4px 0;">${s.pwErr}</p>` : nothing}
      <button class="primary" style="margin-top: 8px; min-width: 200px;"
        ?disabled=${s.busy || !s.pwOld || s.pwNew.length < 12 || s.pwNew !== s.pwConfirm}
        @click=${() => void changePassword()}>
        ${s.busy
          ? html`<span data-lang="en">Updating…</span><span data-lang="jp">更新中…</span>`
          : html`<span data-lang="en">Change password</span><span data-lang="jp">パスワード変更</span>`}
      </button>
    </section>
  `;
}

function recoveryCodesPanel(): TemplateResult {
  if (appState.user?.role !== "SUPER_USER") return html``;
  void loadRecoveryStatus();
  return html`
    <section class="card-panel">
      <h2 class="section-h2">
        <span data-lang="en">Recovery codes</span><span data-lang="jp">リカバリコード</span>
      </h2>
      <p class="section-help">
        <span data-lang="en">Single-use codes to sign in if you lose your authenticator app. Each code works once.</span><span data-lang="jp">認証アプリを紛失した場合にサインインする使い捨てコード。各コードは 1 回のみ使用できます。</span>
      </p>
      ${s.rcStatus ? html`
        <p style="font-size: 14px; margin: 8px 0 16px;">
          <span data-lang="en"><b>${s.rcStatus.remaining}</b> of <b>${s.rcStatus.total}</b> codes remaining</span><span data-lang="jp">残り <b>${s.rcStatus.remaining}</b> / <b>${s.rcStatus.total}</b> コード</span>
          ${s.rcStatus.remaining <= 2 ? html`<span style="color: var(--status-error); margin-left: 8px;">
            <span data-lang="en">- consider regenerating</span><span data-lang="jp">- 再生成を推奨</span>
          </span>` : nothing}
        </p>
      ` : nothing}
      ${s.rcNewCodes.length > 0 ? html`
        <div style="background: var(--surface-2); padding: 16px; border-radius: var(--radius-2); margin-bottom: 16px;">
          <p style="font-weight: 600; margin: 0 0 12px;">
            <span data-lang="en">Save these 10 codes now. They will not be shown again.</span><span data-lang="jp">この 10 コードを今すぐ保存してください。二度と表示されません。</span>
          </p>
          <pre style="font-family: ui-monospace, Consolas, monospace; font-size: 14px; line-height: 1.8; margin: 0;">${s.rcNewCodes.join("\n")}</pre>
          <div style="margin-top: 12px; display: flex; gap: 8px;">
            <button class="ghost-btn" @click=${() => copyToClipboard(s.rcNewCodes.join("\n"))}>
              <span data-lang="en">Copy all</span><span data-lang="jp">すべてコピー</span>
            </button>
            <button class="ghost-btn" @click=${() => { s.rcNewCodes = []; rerender(); }}>
              <span data-lang="en">I have saved them</span><span data-lang="jp">保存しました</span>
            </button>
          </div>
        </div>
      ` : nothing}
      ${!s.rcShowRegen ? html`
        <button class="ghost-btn" @click=${() => { s.rcShowRegen = true; rerender(); }}>
          <span data-lang="en">Regenerate codes</span><span data-lang="jp">コードを再生成</span>
        </button>
      ` : html`
        <div class="form-row" style="max-width: 360px;">
          <label>
            <span data-lang="en">Current 2FA code (to confirm)</span><span data-lang="jp">現在の 2FA コード (確認用)</span>
          </label>
          <input type="text" inputmode="numeric" pattern="[0-9]{6}" maxlength="6"
            autocomplete="one-time-code"
            .value=${s.rcRegenCode}
            @input=${(e: InputEvent) => {
              s.rcRegenCode = (e.target as HTMLInputElement).value.replace(/\D/g, "").slice(0, 6);
              rerender();
            }} />
        </div>
        <div style="display: flex; gap: 8px; margin-top: 8px;">
          <button class="primary" ?disabled=${s.busy || s.rcRegenCode.length !== 6}
            @click=${() => void regenerateRecoveryCodes()}>
            <span data-lang="en">Generate new codes</span><span data-lang="jp">新しいコードを生成</span>
          </button>
          <button class="ghost-btn" @click=${() => { s.rcShowRegen = false; s.rcRegenCode = ""; rerender(); }}>
            <span data-lang="en">Cancel</span><span data-lang="jp">キャンセル</span>
          </button>
        </div>
      `}
    </section>
  `;
}

export function renderSettings(): TemplateResult {
  const mustChange = appState.user?.must_change_pw === true;
  // When a provisioned user has must_change_pw=true, restrict the page
  // to the Change Master Password panel only and show a banner explaining
  // they cannot access the vault until they rotate the temporary password.
  if (mustChange) {
    return html`
      <main class="page">
        <h1 class="page-h1">
          <span data-lang="en">Set your master password</span><span data-lang="jp">マスターパスワードを設定</span>
        </h1>
        <div style="padding: 14px 18px; background: rgba(217,119,6,0.10); border: 1px solid rgba(217,119,6,0.30); border-radius: 12px; margin-bottom: 20px;">
          <p style="margin: 0; color: var(--status-pending); font-weight: 700;">
            <span data-lang="en">Your account was created with a temporary password. You must set a personal master password before you can access the vault.</span><span data-lang="jp">あなたのアカウントは仮パスワードで作成されました。Vault にアクセスする前に個人のマスターパスワードを設定してください。</span>
          </p>
        </div>
        ${changePasswordPanel()}
      </main>
    `;
  }
  return html`
    <main class="page">
      <h1 class="page-h1">
        <span data-lang="en">Settings</span><span data-lang="jp">設定</span>
      </h1>

      <section class="card-panel">
        <h2 class="section-h2">
          <span data-lang="en">Two-factor authentication</span><span data-lang="jp">二要素認証</span>
        </h2>
        <p class="section-help">
          <span data-lang="en">Adds a 6-digit code from Google Authenticator (or any compatible app) at login. Required for the Super User.</span><span data-lang="jp">ログイン時に Google Authenticator（または互換アプリ）の 6 桁コードを追加します。Super User は必須です。</span>
        </p>

        ${!s.totpUri
          ? html`<button class="primary" ?disabled=${s.busy}
              @click=${() => void enrollTotp()}>
              ${s.busy
                ? html`<span data-lang="en">Generating…</span><span data-lang="jp">生成中…</span>`
                : s.totpEnrolled
                  ? html`<span data-lang="en">Configure new device</span><span data-lang="jp">新しいデバイスを設定</span>`
                  : html`<span data-lang="en">Enable 2FA</span><span data-lang="jp">2FA を有効にする</span>`}
            </button>`
          : html`
              <div class="totp-grid">
                <div class="totp-qr">
                  ${s.qrDataUrl
                    ? html`<img src=${s.qrDataUrl} alt="TOTP QR code" />`
                    : html`<p>
                        <span data-lang="en">Generating QR…</span><span data-lang="jp">QR コードを生成中…</span>
                      </p>`}
                </div>
                <div class="totp-meta">
                  <p class="section-help">
                    <span data-lang="en">Open Google Authenticator → tap <b>+</b> → <b>Scan QR</b>. Alternatively, tap <b>Enter setup key</b> and paste the key below.</span><span data-lang="jp">Google Authenticator を開く → <b>+</b> をタップ → <b>QR コードをスキャン</b>。または <b>セットアップキーを入力</b> をタップして下記キーを貼り付けてください。</span>
                  </p>
                  <div class="kv-row">
                    <label>
                      <span data-lang="en">Account</span><span data-lang="jp">アカウント</span>
                    </label>
                    <div class="kv-val mono">Omakase Vault</div>
                  </div>
                  <div class="kv-row">
                    <label>
                      <span data-lang="en">Key (base32)</span><span data-lang="jp">キー (base32)</span>
                    </label>
                    <div class="kv-val mono copyable"
                      title="${appState.lang === "en" ? "Click to copy" : "クリックでコピー"}"
                      @click=${() => copyToClipboard(s.totpSecret)}>${s.totpSecret}</div>
                    <button class="ghost-btn" @click=${() => copyToClipboard(s.totpSecret)}>
                      <span data-lang="en">Copy</span><span data-lang="jp">コピー</span>
                    </button>
                  </div>
                  <div class="kv-row">
                    <label>
                      <span data-lang="en">Type</span><span data-lang="jp">タイプ</span>
                    </label>
                    <div class="kv-val mono">Time-based (30 s, 6 digits, SHA1)</div>
                  </div>
                  <details class="raw-uri">
                    <summary>
                      <span data-lang="en">Full otpauth URI</span><span data-lang="jp">otpauth URI 全文</span>
                    </summary>
                    <div class="kv-row">
                      <div class="kv-val mono" style="word-break: break-all;"
                        @click=${() => copyToClipboard(s.totpUri)}>${s.totpUri}</div>
                      <button class="ghost-btn" @click=${() => copyToClipboard(s.totpUri)}>
                        <span data-lang="en">Copy</span><span data-lang="jp">コピー</span>
                      </button>
                    </div>
                  </details>
                  <div class="form-row" style="margin-top: 16px;">
                    <label>
                      <span data-lang="en">6-digit verification code</span><span data-lang="jp">6 桁の確認コード</span>
                    </label>
                    <input type="text" inputmode="numeric" pattern="[0-9]{6}" maxlength="6"
                      autocomplete="one-time-code"
                      .value=${s.totpCode}
                      @input=${(e: InputEvent) => {
                        s.totpCode = (e.target as HTMLInputElement).value.replace(/\D/g, "").slice(0, 6);
                        rerender();
                      }}
                      @keydown=${(e: KeyboardEvent) => e.key === "Enter" && void verifyTotp()} />
                  </div>
                  <button class="primary" style="margin-top: 12px; min-width: 200px;"
                    ?disabled=${s.busy || s.totpCode.length !== 6}
                    @click=${() => void verifyTotp()}>
                    ${s.busy
                      ? html`<span data-lang="en">Verifying…</span><span data-lang="jp">確認中…</span>`
                      : html`<span data-lang="en">Confirm 2FA</span><span data-lang="jp">2FA を確認</span>`}
                  </button>
                </div>
              </div>
            `}
      </section>

      ${recoveryCodesPanel()}

      ${changePasswordPanel()}

      <section class="card-panel" style="display:none;">
        <h2 class="section-h2">
          <span data-lang="en">Change master password</span><span data-lang="jp">マスターパスワードの変更</span>
        </h2>
        <p class="section-help">
          <span data-lang="en">Replaces the master password. Vault items stay accessible (the internal key does not change). All other sessions are revoked and you will be signed out.</span><span data-lang="jp">マスターパスワードを変更します。Vault のアイテムは引き続きアクセス可能です（内部鍵は変わりません）。他のすべてのセッションが取り消され、再度ログインが必要になります。</span>
        </p>
        <p class="section-help" style="color: var(--status-pending);">
          <span data-lang="en">Interface coming soon.</span><span data-lang="jp">インターフェース準備中。</span>
        </p>
      </section>

      ${void nothing}
    </main>
  `;
}
